import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  Fragment,
} from 'react';
import ScrollCueBox, { ScrollCueFinalState } from './ScrollCueBox';
import Post from './PostComponent';
import { ErrorBoundary } from '@buzzfeed/react-components';

function InfiniteScroll({ previousPath = '', disableDefaultIS }) {
  const [urls, setUrls] = useState([]);
  const [posts, setPosts] = useState([]);
  const [pendingPost, setPendingPost] = useState(null);
  const [finalState, setFinalState] = useState(null);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const [prevPathForNextPost, setPrevPathForNextPost] = useState(previousPath);

  const isMounted = useRef(true);
  const isSafari =
    typeof window !== 'undefined' &&
    /Safari/.test(navigator.userAgent) &&
    !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR/.test(navigator.userAgent);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchBuzzData = useCallback(async (url, referrer = '') => {
    try {
      if (typeof url !== 'string') return null;
      const res = await fetch(`${url}.json`);
      if (!res.ok) return null;

      const json = await res.json();
      const buzz = json.buzz || json;
      if (!buzz?.id || !buzz?.metadata?.title) return null;

      return {
        id: buzz.id,
        buzz: { ...buzz, referrer },
        subbuzzData: json.subbuzzes ?? { subbuzzes: [], assets: {} },
        visible: true,
        isActive: false,
      };
    } catch {
      return null;
    }
  }, []);

  const loadInitial = useCallback(async () => {
    if (hasLoadedInitial || !isMounted.current) return;

    const res = await fetch(
      'https://recsys-api.buzzfeed.com/web/shopping-package-internal?page_size=10'
    );
    const data = await res.json();
    const cleanUrls = (data.results || [])
      .map((r) => r.url)
      .filter((u) => typeof u === 'string');

    setUrls(cleanUrls.slice(1));

    const first = await fetchBuzzData(cleanUrls[0], previousPath);

    if (first && isMounted.current) {
      setPrevPathForNextPost(
        first.buzz?.canonical_path || first.buzz?.canonical_url || ''
      );
      setPendingPost(first);
    }
  }, [fetchBuzzData, previousPath, hasLoadedInitial]);

  const loadNext = useCallback(async () => {
    if (urls.length === 0) return;

    const [nextUrl, ...remaining] = urls;
    if (isMounted.current) setUrls(remaining);

    const nextPost = await fetchBuzzData(nextUrl, prevPathForNextPost);
    if (nextPost && isMounted.current) {
      setPrevPathForNextPost(
        nextPost.buzz?.canonical_path || nextPost.buzz?.canonical_url || ''
      );
      setPendingPost(nextPost);
    }
  }, [urls, fetchBuzzData, prevPathForNextPost]);

  useEffect(() => {
    if (!pendingPost || !isMounted.current) return;

    const raf = requestAnimationFrame(() => {
      if (!isMounted.current) return;

      setPosts((prev) => {
        if (prev.find((p) => p.buzz?.id === pendingPost.buzz?.id)) return prev;

        // logic to disable\enable ads only on one post
        const prevPosts = [...prev];
        if (prevPosts[prevPosts.length - 1]) {
          const prevPost = prevPosts[prevPosts.length - 1];
          prevPost.isActive = false;
          prevPosts[prevPosts.length - 1] = prevPost;
        }

        pendingPost.isActive = true;

        return [...prevPosts, pendingPost];
      });

      setHasLoadedInitial(true);
      setPendingPost(null);

      // Prevent Safari from jumping to bottom by ensuring reasonable scroll position
      if (isSafari && isMounted.current && hasLoadedInitial === false) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            const currentScroll = window.scrollY;
            if (currentScroll < 100 || currentScroll > window.innerHeight * 2) {
              window.scrollTo({ top: Math.min(400, window.innerHeight * 0.6), behavior: 'auto' });
            }
          }, 100);
        });
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [pendingPost, isSafari, hasLoadedInitial]);

  useEffect(() => {
    (async () => {
      try {
        await loadInitial();
      } catch (e) {
        console.error('loadInitial failed:', e);
      }
    })();
  }, [loadInitial]);

  const hidePostAtIndex = (index) => {
    setPosts((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const updated = [...prev];
      updated[index] = { ...updated[index], visible: false };
      return updated;
    });
  };

  const handleExpand = useCallback(
    async (index) => {
      const finishedPost = posts[index];

      if (typeof disableDefaultIS === 'function') {
        disableDefaultIS(true);
      }

      if (isSafari && finishedPost && isMounted.current) {
        setFinalState({ ...finishedPost, previousPath: prevPathForNextPost });
        hidePostAtIndex(index);
      }

      await loadNext();

      // Safari scroll management to prevent jumping to bottom of new articles
      if (isSafari && isMounted.current) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            const articles = document.querySelectorAll('[data-infinite-post]');
            const lastArticle = articles[articles.length - 1];

            if (lastArticle) {
              const articleRect = lastArticle.getBoundingClientRect();
              const articleTop = articleRect.top + window.scrollY;
              const targetScroll = articleTop + Math.min(300, articleRect.height * 0.2);
              window.scrollTo({ top: targetScroll, behavior: 'smooth' });
            } else {
              window.scrollTo({ top: Math.min(400, window.innerHeight * 0.6), behavior: 'auto' });
            }
          }, 50);
        });
      }
    },
    [isSafari, posts, loadNext, disableDefaultIS, prevPathForNextPost]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const originalRemoveChild = Node.prototype.removeChild;

    Node.prototype.removeChild = function (child) {
      try {
        if (this.contains && this.contains(child)) {
          return originalRemoveChild.call(this, child);
        } else if (this.parentNode && this.parentNode.contains && this.parentNode.contains(child)) {
          return this.parentNode.removeChild(child);
        } else {
          return child;
        }
      } catch (err) {
        return child;
      }
    };

    return () => {
      Node.prototype.removeChild = originalRemoveChild;
    };
  }, []);

  return (
    <div>
      {finalState && (
        <ScrollCueFinalState
          key={`final-${finalState.id}`}
          previousPath={finalState.previousPath}
        />
      )}

      {posts.map((post, index) => {
        if (!post.visible) return null;
        const renderKey = `${post.buzz?.id}-${post.buzz?.canonical_path || ''}`;

        return (
          <Fragment key={renderKey}>
            <ErrorBoundary fallback={null}>
              <Post
                buzz={post.buzz}
                subbuzzData={post.subbuzzData}
                index={index}
                isActivePost={post.isActive}
              />
            </ErrorBoundary>

            {/* Only show ScrollCueBox if it's not the final post */}
            {!(index === posts.length - 1 && urls.length === 0) && (
              <ScrollCueBox
                onExpandComplete={() => handleExpand(index)}
                onOffscreenScrollDown={() => {
                  if (isSafari) return;

                  const finishedPost = posts[index];
                  if (!finishedPost || !isMounted.current) return;

                  setFinalState({ ...finishedPost, previousPath: prevPathForNextPost });
                  hidePostAtIndex(index);
                }}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

export default InfiniteScroll;
